import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook genérico para cargar un catálogo (actividades, municipios, ciclos, parcelas...).
 * `loader` es una función que devuelve una Promise con los datos (normalmente `repository.list`).
 * Contrato: { data, status: 'idle'|'loading'|'success'|'error', error, reload }
 * `lazy: true` no carga al montar: la UI llama `reload()` cuando lo necesita (ej. al abrir un select).
 * Seguro ante desmontaje y respuestas fuera de orden.
 */
export const useCatalogo = (loader, deps = [], { lazy = false } = {}) => {
  const [state, setState] = useState({ data: [], status: 'idle', error: null });
  const mounted = useRef(true);
  const requestId = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const id = ++requestId.current;
    setState(prev => ({ ...prev, status: 'loading', error: null }));
    return Promise.resolve()
      .then(() => loaderRef.current())
      .then(
        data => {
          if (mounted.current && id === requestId.current) setState({ data: data || [], status: 'success', error: null });
        },
        error => {
          if (mounted.current && id === requestId.current) setState({ data: [], status: 'error', error });
        }
      );
  }, []);

  useEffect(() => {
    if (!lazy) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data: state.data, status: state.status, error: state.error, reload: load };
};

export default useCatalogo;
